// Typed Relationships API client for the web layer (three-tier client model
// Stage 4, ADR-0015 D4/D7).
//
// Wraps the `relationshipsContract` endpoints via the shared `api` client
// and exposes react-query hooks. Mutations route their `{ status, body }`
// envelope through `runStaffEndpoint`; relationship mutations ALSO
// invalidate the contacts / clients / suitability-records caches, because
// their `hasSuitabilityRecord` / `hasLinkedContact` chips are real
// projections of relationship rows now.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, runStaffEndpoint } from '@/lib/api';
import type {
    CompleteClientLinksRequest,
    CompleteClientLinksResponse,
    CreateRelationshipRequest,
    CreateRelationshipRoleRequest,
    ListBookRelationshipsResponse,
    ListRelationshipRolesResponse,
    ListRelationshipsResponse,
    StaffActionResult,
    UpdateRelationshipRequest,
    UpdateRelationshipRoleRequest,
} from '@septima/contract';
import { contactsKeys } from '@/lib/contacts/hooks';
import { clientsKeys } from '@/lib/clients/hooks';
import { clientBookKeys } from '@/lib/client-book/hooks';
import { suitabilityRecordsKeys } from '@/lib/suitability-records/hooks';

export const relationshipsKeys = {
    all: ['relationships'] as const,
    roles: ['relationships', 'roles'] as const,
    forParty: (endpointId: string) => ['relationships', 'endpoint', endpointId] as const,
    book: ['relationships', 'book'] as const,
};

function assertOk(result: StaffActionResult): StaffActionResult {
    if (!result.ok) throw new Error(result.error ?? 'Request failed');
    return result;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** The role catalogue — dropdowns filter `active`; the admin card reads all. */
export function useRelationshipRoles() {
    return useQuery({
        queryKey: relationshipsKeys.roles,
        queryFn: async (): Promise<ListRelationshipRolesResponse> => {
            const res = await api.relationships.listRoles();
            if (res.status !== 200) throw new Error(`Roles failed: ${res.status}`);
            return res.body;
        },
        staleTime: 60_000,
    });
}

/** Every relationship an endpoint carries, both directions merged (PRD §10). */
export function useRelationships(endpointId: string | undefined) {
    return useQuery({
        queryKey: relationshipsKeys.forParty(endpointId ?? ''),
        enabled: Boolean(endpointId),
        queryFn: async (): Promise<ListRelationshipsResponse> => {
            const res = await api.relationships.listRelationships({
                query: { endpointId: endpointId! },
            });
            if (res.status !== 200) throw new Error(`Relationships failed: ${res.status}`);
            return res.body;
        },
        staleTime: 5_000,
    });
}

/**
 * Every relationship in the caller's book (SEP-176). Scope is server-side
 * (`Relationship` is `deskRowScope: 'book-owner'`) — this hook passes no
 * owner and must never grow one.
 *
 * It takes no arguments on purpose: the endpoint returns the whole readable
 * set unpaged, so the page's endpoint-type filters run in the browser. Making
 * them query arguments would put them in the react-query key, and every filter
 * change would blank the header cards for a round trip.
 */
export function useBookRelationships() {
    return useQuery({
        queryKey: relationshipsKeys.book,
        queryFn: async (): Promise<ListBookRelationshipsResponse> => {
            const res = await api.relationships.listBookRelationships();
            if (res.status !== 200) throw new Error(`Relationships failed: ${res.status}`);
            return res.body;
        },
        staleTime: 5_000,
    });
}

// ---------------------------------------------------------------------------
// Mutations (via runStaffEndpoint)
// ---------------------------------------------------------------------------

function useInvalidateRelationshipConsumers() {
    const qc = useQueryClient();
    return () => {
        void qc.invalidateQueries({ queryKey: relationshipsKeys.all });
        // The linked-state chips on all three directories are projections of
        // relationship rows — refresh them together.
        void qc.invalidateQueries({ queryKey: contactsKeys.all });
        void qc.invalidateQueries({ queryKey: clientsKeys.all });
        void qc.invalidateQueries({ queryKey: suitabilityRecordsKeys.all });
        // The Client Book detail page reads its own key, and its Relationships
        // tab mounts this panel (SEP-244). Without this line the tab keeps a
        // deleted relationship on the screen until the next refetch.
        void qc.invalidateQueries({ queryKey: clientBookKeys.all });
    };
}

export interface RelationshipMutationOutcome {
    relationshipId: string;
    /** Warn-never-block notices (PRD §9.3) — render amber, then proceed. */
    warnings: string[];
}

export function useCreateRelationship() {
    const invalidate = useInvalidateRelationshipConsumers();
    return useMutation({
        mutationFn: async (
            input: CreateRelationshipRequest,
        ): Promise<RelationshipMutationOutcome> => {
            const result = assertOk(
                await runStaffEndpoint(api.relationships.createRelationship({ body: input })),
            );
            const body = result as { relationshipId?: string; warnings?: string[] };
            return {
                relationshipId: body.relationshipId ?? '',
                warnings: body.warnings ?? [],
            };
        },
        onSuccess: invalidate,
    });
}

/**
 * Correct a relationship in place (SEP-214) — roles, start date and note. The
 * endpoints are not in the patch and never will be: a different pairing is a
 * different relationship.
 */
export function useUpdateRelationship() {
    const invalidate = useInvalidateRelationshipConsumers();
    return useMutation({
        mutationFn: async (input: {
            relationshipId: string;
            patch: UpdateRelationshipRequest;
        }): Promise<RelationshipMutationOutcome> => {
            const result = assertOk(
                await runStaffEndpoint(
                    api.relationships.updateRelationship({
                        params: { relationshipId: input.relationshipId },
                        body: input.patch,
                    }),
                ),
            );
            const body = result as { relationshipId?: string; warnings?: string[] };
            return {
                relationshipId: body.relationshipId ?? '',
                warnings: body.warnings ?? [],
            };
        },
        onSuccess: invalidate,
    });
}

export function useEndRelationship() {
    const invalidate = useInvalidateRelationshipConsumers();
    return useMutation({
        mutationFn: async (relationshipId: string): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.relationships.endRelationship({
                        params: { relationshipId },
                        body: {},
                    }),
                ),
            );
        },
        onSuccess: invalidate,
    });
}

/**
 * Delete a relationship (SEP-244.4).
 *
 * A delete is PERMANENT and it keeps no background copy. `useEndRelationship`
 * above is the other affordance: it sets an end date, and the row stays as
 * history. The audit ledger records the delete and the person who did it.
 */
export function useDeleteRelationship() {
    const invalidate = useInvalidateRelationshipConsumers();
    return useMutation({
        mutationFn: async (relationshipId: string): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.relationships.deleteRelationship({
                        params: { relationshipId },
                        body: {},
                    }),
                ),
            );
        },
        onSuccess: invalidate,
    });
}

/**
 * Complete the links of one Client Book row as ONE operation (SEP-300). The
 * server runs every step in one transaction, so a failure saves nothing. On
 * success the shared invalidation refreshes the tri-tier row at once, because
 * `clientBookKeys.all` covers the tri-tier key.
 */
export function useCompleteClientLinks() {
    const invalidate = useInvalidateRelationshipConsumers();
    return useMutation({
        mutationFn: async (
            input: CompleteClientLinksRequest,
        ): Promise<CompleteClientLinksResponse> => {
            const result = assertOk(
                await runStaffEndpoint(api.relationships.completeClientLinks({ body: input })),
            );
            const body = result as Partial<CompleteClientLinksResponse>;
            return {
                ok: true,
                outcomes: body.outcomes ?? [],
                warnings: body.warnings ?? [],
            };
        },
        onSuccess: invalidate,
    });
}

// ---------------------------------------------------------------------------
// Admin role-catalogue mutations
// ---------------------------------------------------------------------------

export function useCreateRelationshipRole() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateRelationshipRoleRequest): Promise<void> => {
            assertOk(await runStaffEndpoint(api.relationships.createRole({ body: input })));
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: relationshipsKeys.roles }),
    });
}

export function useUpdateRelationshipRole() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            roleId: string;
            patch: UpdateRelationshipRoleRequest;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.relationships.updateRole({
                        params: { roleId: input.roleId },
                        body: input.patch,
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: relationshipsKeys.roles }),
    });
}

export function useDeactivateRelationshipRole() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (roleId: string): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.relationships.deactivateRole({ params: { roleId }, body: {} }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: relationshipsKeys.roles }),
    });
}
